import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search, UserRound } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

// Selector estándar de colaborador (Regla G-001). Selección EXCLUSIVA desde el
// directorio de la empresa activa: búsqueda por nombre/apellido, foto, cargo,
// área, estado y autocompletado. Nunca se escriben nombres a mano.
//
// Props:
//   value: user_id seleccionado (string) | null
//   onChange: (employee|null) => void  — devuelve el objeto completo del empleado
//   scope: "" (todo el directorio) | "managed" (aplica alcance por rol, G-003)
//   placeholder, disabled, includeInactive, testid
export function EmployeePicker({
  value,
  onChange,
  scope = "",
  placeholder = "Seleccionar colaborador…",
  disabled = false,
  includeInactive = false,
  testid = "employee-picker",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (scope) params.set("scope", scope);
    if (includeInactive) params.set("include_inactive", "true");
    api
      .get(`/directory?${params.toString()}`)
      .then((r) => { if (active) setEmployees(r.data.employees || []); })
      .catch(() => { if (active) setEmployees([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [scope, includeInactive]);

  const selected = useMemo(() => employees.find((e) => e.id === value) || null, [employees, value]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(term) ||
        (e.first_name || "").toLowerCase().includes(term) ||
        (e.last_name || "").toLowerCase().includes(term) ||
        (e.puesto || "").toLowerCase().includes(term) ||
        (e.area || "").toLowerCase().includes(term)
    );
  }, [q, employees]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          disabled={disabled}
          className={cn(
            "w-full flex items-center gap-2.5 rounded-xl border border-navy/15 bg-white px-3 py-2 text-left text-sm transition-colors",
            disabled ? "opacity-60 cursor-not-allowed" : "hover:border-violet"
          )}
        >
          {selected ? (
            <>
              <Avatar className="h-7 w-7 shrink-0">
                {selected.avatar ? <AvatarImage src={mediaUrl(selected.avatar)} alt={selected.name} /> : null}
                <AvatarFallback className="text-[10px] bg-violet/10 text-violet">
                  {(selected.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-navy">{selected.name}</span>
                <span className="block truncate text-xs text-navy/50">
                  {[selected.puesto, selected.area].filter(Boolean).join(" · ") || "—"}
                </span>
              </span>
            </>
          ) : (
            <>
              <UserRound className="h-4 w-4 shrink-0 text-navy/40" />
              <span className="flex-1 text-navy/50">{placeholder}</span>
            </>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-navy/30" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center gap-2 border-b border-navy/10 px-3 py-2">
          <Search className="h-4 w-4 text-navy/40" />
          <input
            autoFocus
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, apellido, cargo o área…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-navy/40"
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {loading && <div className="px-3 py-4 text-sm text-navy/40">Cargando directorio…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-4 text-sm text-navy/40" data-testid={`${testid}-empty`}>
              Sin colaboradores que coincidan.
            </div>
          )}
          {!loading &&
            filtered.map((e) => (
              <button
                key={e.id}
                type="button"
                data-testid={`${testid}-option-${e.id}`}
                onClick={() => { onChange && onChange(e); setOpen(false); setQ(""); }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-violet/5 transition-colors"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  {e.avatar ? <AvatarImage src={mediaUrl(e.avatar)} alt={e.name} /> : null}
                  <AvatarFallback className="text-[10px] bg-violet/10 text-violet">
                    {(e.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-navy">{e.name}</span>
                  <span className="block truncate text-xs text-navy/50">
                    {[e.puesto, e.area].filter(Boolean).join(" · ") || "—"}
                  </span>
                </span>
                {e.estado === "inactivo" && (
                  <Badge variant="outline" className="text-[10px] border-navy/20 text-navy/40">Inactivo</Badge>
                )}
                {value === e.id && <Check className="h-4 w-4 text-violet" />}
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default EmployeePicker;
